/**
 * NymoArt Container - JavaScript v2.0.1
 * 
 * Visibility container that shows/hides based on code detection events
 * 
 * Visibility modes:
 * - always: Always visible
 * - before_code: Visible until code is detected, then hides
 * - after_code: Hidden until code is detected, then shows
 * 
 * @requires NymoArt (nymo-base.js)
 */

(function() {
  'use strict';
  
  // Wait for NymoArt to be available
  function initContainer() {
    if (typeof NymoArt === 'undefined') {
      console.warn('NymoArt Container: Waiting for NymoArt base...');
      setTimeout(initContainer, 50);
      return;
    }
    
    if (customElements.get('nymo-container')) {
      return; // Already registered
    }
    
    customElements.define('nymo-container', class NymoContainer extends HTMLElement {
      
      constructor() {
        super();
        
        // State - don't access NymoArt here yet
        this.state = {
          isVisible: true,
          codeDetected: false,
          initialized: false
        };
        
        // Bound event handlers
        this._onCodeDetected = this._handleCodeDetected.bind(this);
        this._onCodeReset = this._handleCodeReset.bind(this);
      }
      
      connectedCallback() {
        // Now we can access NymoArt
        this.moduleId = this.dataset.moduleId || NymoArt.utils.uniqueId('container-');
        
        // Configuration from data attributes
        this.config = {
          visibility: this.dataset.visibility || 'always',
          animation: this.dataset.animation || 'fade'
        };
        
        // Add animation class
        this.classList.add(`nymo-container--${this.config.animation}`);
        
        // Set initial visibility based on mode
        this._setInitialVisibility();
        
        // Subscribe to events
        if (this.config.visibility !== 'always') {
          this._unsubscribeDetected = NymoArt.events.on('nymo:code:detected', this._onCodeDetected);
          this._unsubscribeReset = NymoArt.events.on('nymo:code:reset', this._onCodeReset);
        }
        
        // Register module
        NymoArt.modules.register(`container-${this.moduleId}`, this);
        
        this.state.initialized = true;
        NymoArt.log.info(`Container initialized: ${this.moduleId}`, this.config);
      }
      
      disconnectedCallback() {
        // Cleanup subscriptions
        if (this._unsubscribeDetected) this._unsubscribeDetected();
        if (this._unsubscribeReset) this._unsubscribeReset();
        
        if (this.state.initialized) {
          NymoArt.modules.unregister(`container-${this.moduleId}`);
        }
      }
      
      _setInitialVisibility() {
        // Find parent grid item (multi-column__item or similar)
        this._parentItem = this.closest('.multi-column__item, .grid__item, [class*="column"]');
        
        switch (this.config.visibility) {
          case 'before_code':
            // Visible initially
            this.state.isVisible = true;
            this._setHidden(false);
            this.classList.add('nymo-container--before-code');
            this.classList.add('nymo-container--visible');
            break;
            
          case 'after_code':
            // Hidden initially
            this.state.isVisible = false;
            this._setHidden(true);
            this.classList.add('nymo-container--after-code');
            this.classList.add('nymo-container--hidden');
            break;
            
          case 'always':
          default:
            // Always visible
            this.state.isVisible = true;
            this._setHidden(false);
            break;
        }
      }
      
      /**
       * Set hidden state on container AND parent grid item
       */
      _setHidden(hidden) {
        this.hidden = hidden;
        
        // Also hide parent grid item so it doesn't take space
        if (this._parentItem) {
          this._parentItem.hidden = hidden;
          this._parentItem.style.display = hidden ? 'none' : '';
        }
      }
      
      _handleCodeDetected(data) {
        console.log(`📦 Container ${this.moduleId}: Code detected`, data);
        this.state.codeDetected = true;
        
        switch (this.config.visibility) {
          case 'before_code':
            this.hide();
            break;
            
          case 'after_code':
            this.show();
            break;
        }
      }
      
      _handleCodeReset() {
        console.log(`📦 Container ${this.moduleId}: Code reset`);
        this.state.codeDetected = false;
        
        switch (this.config.visibility) {
          case 'before_code':
            this.show();
            break;
            
          case 'after_code':
            this.hide();
            break;
        }
      }
      
      /**
       * Show the container with animation
       */
      show() {
        if (this.state.isVisible) return;
        
        console.log(`📦 Container ${this.moduleId}: Showing`);
        
        // Remove hidden from container AND parent
        this._setHidden(false);
        
        // Add showing class for animation start state
        this.classList.add('nymo-container--showing');
        this.classList.remove('nymo-container--hiding', 'nymo-container--hidden');
        
        // Force reflow to ensure animation plays
        void this.offsetHeight;
        
        // Remove showing class and add visible for animation end state
        requestAnimationFrame(() => {
          this.classList.remove('nymo-container--showing');
          this.classList.add('nymo-container--visible');
        });
        
        this.state.isVisible = true;
        
        // Emit event
        NymoArt.events.emit('nymo:container:shown', {
          moduleId: this.moduleId
        });
      }
      
      /**
       * Hide the container with animation
       */
      hide() {
        if (!this.state.isVisible) return;
        
        console.log(`📦 Container ${this.moduleId}: Hiding`);
        
        // Add hiding class for animation
        this.classList.add('nymo-container--hiding');
        this.classList.remove('nymo-container--visible', 'nymo-container--showing');
        
        // Wait for animation to complete, then hide
        const duration = parseFloat(getComputedStyle(this).getPropertyValue('--nymo-transition-duration')) * 1000 || 300;
        
        this.state.isVisible = false;
        
        setTimeout(() => {
          if (!this.state.isVisible) {
            // Hide container AND parent
            this._setHidden(true);
            this.classList.remove('nymo-container--hiding');
            this.classList.add('nymo-container--hidden');
          }
        }, duration);
        
        // Emit event
        NymoArt.events.emit('nymo:container:hidden', {
          moduleId: this.moduleId
        });
      }
      
      /**
       * Toggle visibility
       */
      toggle() {
        if (this.state.isVisible) {
          this.hide();
        } else {
          this.show();
        }
      }
    });
    
    console.log('📦 NymoArt Container v2.0.1 loaded');
  }
  
  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContainer);
  } else {
    initContainer();
  }
})();
